import {
	Marked,
	Renderer,
	Token,
	Tokens
} from "marked";
import {
	ReactElement,
	ReadonlyRefObject,
	RefObject,
	useMemo,
	useRef,
	useState
} from "react";
import { RawHtml } from "../RawHtml/RawHtml";
import styles from "./MarkdownViewer.module.less";
import {
	useEffectSkipFirstRender,
	useSynchronizedRef,
	useValueEqualMemo
} from "client/Hooks";
import { Guid } from "client_legacy/ndaw/guid";
import {
	getClientId,
	ObjectWithClientId,
	setClientId,
	tryGetClientId
} from "client/Misc/clientId";
import { htmlEscape } from "client/Misc/htmlUtils";

const allowedProtocols: readonly string[] = [
	"https:",
	"http:"
];

export type MarkdownViewerHeaderKind = "h1" | "h2" | "h3" | "h4";

export type MarkdownViewerHeaderMap = {
	[key in MarkdownViewerHeaderKind]: MarkdownViewerHeaderKind
};

export type MarkdownViewerProps = {
	content: string;
	hasIncompleteContent?: boolean;
	onHasErrorChange?: (hasError: boolean) => void;
	headerMap?: MarkdownViewerHeaderMap;
};

export const MarkdownViewer = (props: MarkdownViewerProps): ReactElement => {
	const {
		content,
		hasIncompleteContent = false,
		onHasErrorChange,
		headerMap: headerMapFromProps
	} = props;

	const headerMap = useValueEqualMemo(
		(): MarkdownViewerHeaderMap | undefined => headerMapFromProps,
		[headerMapFromProps]
	);

	const [finalHtml, setFinalHtml] = useState<OptionalModel<string>>(
		(): OptionalModel<string> => {
			const result: TryResult<OptionalModel<string>> = getFinalHtml(
				content,
				hasIncompleteContent,
				headerMap
			);

			if (result.wasSuccessful) {
				return result.result;
			}

			return { isSet: true, value: "" };
		}
	);

	const previouslyReportedErrorState: RefObject<boolean> = useRef<boolean>(false);
	const stableOnHasErrorChangeRef: ReadonlyRefObject<typeof onHasErrorChange> = useSynchronizedRef(onHasErrorChange);

	useEffectSkipFirstRender(
		(): void => {
			const onHasErrorChange: ((hasError: boolean) => void) | undefined = stableOnHasErrorChangeRef.current;

			const result: TryResult<OptionalModel<string>> = getFinalHtml(
				content,
				hasIncompleteContent,
				headerMap
			);

			if (onHasErrorChange) {
				const hasError: boolean = !result.wasSuccessful;

				if (previouslyReportedErrorState.current !== hasError) {
					onHasErrorChange(hasError);
					previouslyReportedErrorState.current = hasError;
				}
			}

			const finalHtml: OptionalModel<string> = result.wasSuccessful
				? result.result
				: { isSet: true, value: "" };

			setFinalHtml(finalHtml);
		},
		[
			content,
			hasIncompleteContent,
			stableOnHasErrorChangeRef,
			headerMap
		]
	);

	const directHtml = useMemo(
		(): string => {
			if (!hasIncompleteContent) {
				return "";
			}

			try {
				return getParsedContent(
					content,
					true,
					headerMap
				);
			} catch(e) {
				console.error(e);
				return "";
			}
		},
		[
			content,
			hasIncompleteContent,
			headerMap
		]
	);

	const html: string = hasIncompleteContent || !finalHtml.isSet
		? directHtml
		: finalHtml.value;

	return (
		<div className={styles.root}>
			<RawHtml html={html} />
		</div>
	);
};
MarkdownViewer.displayName = "MarkdownViewer";

function getFinalHtml(
	content: string,
	hasIncompleteContent: boolean,
	headerMap: MarkdownViewerHeaderMap | undefined
): TryResult<OptionalModel<string>> {
	if (hasIncompleteContent) {
		return {
			wasSuccessful: true,
			result: { isSet: false }
		};
	}

	try {
		return {
			wasSuccessful: true,
			result: {
				isSet: true,
				value: getParsedContent(
					content,
					false,
					headerMap
				)
			}
		};
	} catch(e) {
		return { wasSuccessful: false };
	}
}

const marked = new Marked({
	renderer: {
		checkbox(checkbox: Tokens.Checkbox): string {
			const clientId: Guid | undefined = tryGetClientId(checkbox);

			const idAttribute: string = clientId
				? ` id="${clientId}"`
				: "";

			if (checkbox.checked) {
				return `<input type="checkbox" checked${idAttribute}>`;
			} else {
				return `<input type="checkbox"${idAttribute}>`;
			}
		},
		list(token: Tokens.List): string | false {
			const isTaskList: boolean = token.items
				.every(i => i.task);

			if (!isTaskList) {
				return false;
			}

			const body: string = token.items
				.map(t => this.listitem(t))
				.join("");

			return `<ul data-md-class="task-list">${body}</ul>`;
		},
		listitem(item: Tokens.ListItem): string | false {
			if (!item.task) {
				return false;
			}

			const checkboxToken: ObjectWithClientId<Tokens.Checkbox> = setClientId({
				checked: item.checked ?? false
			});

			return `
				<li data-md-class="task-list-item">
					${this.checkbox(checkboxToken)}
					<label for="${getClientId(checkboxToken)}">${item.text}</label>
				</li>
			`;
		},
		table(token: Tokens.Table): string | false {
			const baseTable: string = (Object.getPrototypeOf(this) as Renderer)
				.table
				.call(this, token);

			return `<div data-md-class="table-wrapper">${baseTable}</div>`;
		},
		link(token: Tokens.Link): string | false {
			const text: string = this.parser
				.parseInline(token.tokens);

			try {
				const url = new URL(token.href);

				if (allowedProtocols.includes(url.protocol)) {
					let titleAttribute: string = "";

					if (token.title) {
						titleAttribute = ` title="${htmlEscape(token.title)}"`;
					}

					return `<a href="${url.href}" target="_blank"${titleAttribute}>${text}</a>`;
				}
			} catch(e) {
				console.error(e);
			}

			return `<span>${text}</span>`;
		},
		html(): string {
			return "";
		}
	}
});

function getParsedContent(
	content: string,
	shouldIgnoreErrors: boolean,
	headerMap: MarkdownViewerHeaderMap | undefined
): string {
	return marked.parse(
		content,
		{
			silent: shouldIgnoreErrors,
			walkTokens: headerMap && applyHeaderMap(headerMap)
		}
	// NOTE type definitions for the library are pretty poor
	// according to documentation the parsing is asynchronous only when "async" option is set to true.
	) as string;
};

function applyHeaderMap(
	headerMap: MarkdownViewerHeaderMap
): (token: Token) => void {
	return (token: Token): void => {
		if (token.type !== "heading") {
			return;
		}

		token.depth = headerKindToDepthMap[
			headerMap[
				getHeaderKindFromDepth(token.depth)
			]
		];
	};
}

const headerKindToDepthMap: { [key in MarkdownViewerHeaderKind]: number; } = {
	"h1": 1,
	"h2": 2,
	"h3": 3,
	"h4": 4
};

function getHeaderKindFromDepth(depth: number): MarkdownViewerHeaderKind {
	switch (depth) {
		case 1:
			return "h1";
		case 2:
			return "h2";
		case 3:
			return "h3";
		case 4:
			return "h4";
		default:
			// NOTE: We do not support headers below h4
			return "h4";
	}
}